import {
  Label,
  Input,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEvent } from "react"
import {
  useCallback,
  useState,
} from "react"
import styles from "./AccountDetails.module.scss"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useUpdateAccount,
  useAccountProperty,
} from "src/hooks"
import { EditFirstnameErrorToast } from "src/toasts/EditFirstnameErrorToast"
import { FirstnameCannotBeEmptyErrorToast } from "src/toasts/FirstnameCannotBeEmptyErrorToast"

const polyglotPrefix = "pages.account.account_details.firstname."
const firstnameId = "first-name-input"

export function Firstname() {
  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const updateAccount = useUpdateAccount()

  const accountId = useAccountProperty(
    "id",
    NaN,
  )
  const currentFirstname = useAccountProperty(
    "firstname",
    "",
  )

  const [
    firstname,
    setFirstname,
  ] = useState<string>(currentFirstname)

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setFirstname(e.target.value)
    },
    [setFirstname],
  )

  const resetFirstname = useCallback(
    () => {
      setFirstname(currentFirstname)
    },
    [currentFirstname],
  )

  const handleFirstnameUpdate = useCallback(
    async () => {
      if (firstname === currentFirstname) {
        return
      }

      // First name cannot be left blank
      if (!firstname) {
        resetFirstname()

        showToast(
          "firstname-cannot-be-empty-error",
          FirstnameCannotBeEmptyErrorToast,
        )
        return
      }

      setIsOverlayLoaderVisible(true)

      try {
        if (!updateAccount) {
          throw new Error("Update account function is not available")
        }

        await updateAccount({
          id: accountId,
          firstname,
        })
      } catch {
        showToast(
          "edit-firstname-error",
          EditFirstnameErrorToast,
        )
      }

      setIsOverlayLoaderVisible(false)
    },
    [
      firstname,
      currentFirstname,
      accountId,
      updateAccount,
      resetFirstname,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const onBlur = useCallback(
    () => {
      void handleFirstnameUpdate()
    },
    [handleFirstnameUpdate],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        void handleFirstnameUpdate()
      }
    },
    [handleFirstnameUpdate],
  )

  return (
    <div>
      <Label
        className={styles.inputLabel}
        htmlFor={firstnameId}
      >
        {polyglot.t(`${polyglotPrefix}label`)}
      </Label>
      <Input
        className={styles.firstnameInput}
        id={firstnameId}
        value={firstname}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoComplete="given-name"
      />
    </div>
  )
}
